import axios, { AxiosError } from "axios";

// Define base API URL - adjust this based on your environment
const baseURL = import.meta.env.VITE_API_URL || "http://dev.nodetool.ai:9800";

// Define a type guard to check for error detail structure
interface ErrorDetail {
  error_key?: string;
  message?: string;
}
const hasErrorDetail = (
  data: unknown
): data is { detail: string | ErrorDetail } => {
  return typeof data === "object" && data !== null && "detail" in data;
};
const detailIsObject = (
  detail: string | ErrorDetail
): detail is ErrorDetail => {
  return typeof detail === "object" && detail !== null;
};

const apiClient = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - will be used to add auth tokens
apiClient.interceptors.request.use(
  (config) => {
    // Get token from storage (will be implemented with auth)
    const token = localStorage.getItem("authToken");

    // If token exists, add to headers
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - map errors to translation keys
apiClient.interceptors.response.use(
  (response) => {
    // Any status code that lie within the range of 2xx cause this function to trigger
    return response;
  },
  (error: AxiosError) => {
    // Any status codes that falls outside the range of 2xx cause this function to trigger
    let errorKey = "api.errors.unknown"; // Default error key

    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      const { status, data } = error.response;
      console.error("API Error Response:", {
        status,
        data,
        config: error.config,
      });

      switch (status) {
        case 400:
          // Use type guard to safely access potential error_key
          if (
            hasErrorDetail(data) &&
            detailIsObject(data.detail) &&
            data.detail.error_key
          ) {
            errorKey = data.detail.error_key;
          } else {
            errorKey = "api.errors.badRequest";
          }
          break;
        case 401:
          errorKey = "api.errors.unauthorized";
          // Still perform redirect for unauthorized
          localStorage.removeItem("authToken");
          // Maybe delay redirect slightly to allow message display?
          window.location.href = "/login";
          break;
        case 402:
          errorKey = "api.errors.paymentRequired";
          break;
        case 404:
          errorKey = "api.errors.notFound";
          break;
        case 429:
          errorKey = "api.errors.rateLimited";
          break;
        case 500:
        case 502:
        case 503:
        case 504:
          errorKey = "api.errors.serverError";
          break;
        default:
          // Log unhandled statuses but use the default unknown key
          if (hasErrorDetail(data)) {
            console.warn(
              "Unhandled API error status:",
              status,
              "Data Detail:",
              data.detail
            );
          } else {
            console.warn("Unhandled API error status:", status, "Data:", data);
          }
          break;
      }
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser
      console.error("Network Error:", error.request);
      errorKey = "api.errors.networkError";
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error("Axios Setup Error:", error.message);
      errorKey = "api.errors.unknown";
    }

    // Reject with a new Error containing the translation key
    return Promise.reject(new Error(errorKey));
  }
);

export default apiClient;
